/* =====================================================================
 * bc-relay-shared.js — the SHARED dataLayer → front-door relay
 *
 * Extracted from snippets/bc-customizer-journey.liquid on 2026-09-05
 * (perf item 27). That snippet was 9.7 KB of inline, uncacheable JavaScript
 * rendered into EVERY page of the site — homepage, blog, collections, cart,
 * account — even though ~85% of it (the customizer journey) can only ever fire
 * on a product page. Inline script bytes are re-downloaded on every navigation
 * and are never in the HTTP cache; an `asset_url` file is fetched once per
 * deploy and served from Shopify's CDN with a content hash.
 *
 * WHAT STAYS HERE (every page)
 *   · the dataLayer.push wrap and its handler registry,
 *   · the /collect envelope + sendBeacon/keepalive-fetch transport,
 *   · the session id, cookie reader and product-handle helper, and the
 *     consent gate + door stamp (both from window.bcConsent, 2026-09-29),
 *   · the `bc_klaviyo_form_engaged` relay — a signup can start anywhere.
 *   · the sibling-product soft-nav `view_item` (2026-09-29, see the handler).
 *
 * WHAT MOVED OUT
 *   · the whole customizer journey → assets/bc-customizer-relay.js, rendered
 *     on product templates only. It registers itself through BcRelay.on().
 *   · `scroll` → NOT relayed at all any more (perf item 23). Scroll depth was
 *     tracked three ways; the page-exit maximum in bc-page-vitals
 *     (`max_scroll_pct`) is the one bc-tracking actually reads — the CRM's
 *     `scroll_depth` is derived from it in src/shared/crm-forward.ts:283 and
 *     the CRM's own `scrollMax` is a Math.max, so four threshold events per
 *     page told it nothing the single exit maximum does not. The dataLayer /
 *     GTM copy from assets/click-events.js is UNTOUCHED — GA4 keeps its
 *     scroll_depth events. THIS IS A CAPTURE-POLICY CHANGE: ~1,169 door events
 *     a day (7% of the lane) stop being recorded as individual signals.
 *
 *     WHO CONSUMES A `scroll` EVENT — all four, not the one this note used to
 *     name (round 1, A6). Three of them read the EVENT ROW, not the value:
 *       1. beyondcarat-crm lib/timeline/summarize.ts — a MAX over scroll
 *          depth. Unaffected: max_scroll_pct is already the max.
 *       2. beyondcarat-crm lib/sales/funnel.ts:82-92 — counts events to place
 *          a visitor at the Visitor stage. Four rows become one.
 *       3. beyondcarat-crm lib/reports/hot-leads.ts:126-165 — a deny-list CTE
 *          over distinct days / distinct sessions and MAX(event_ts) tiers.
 *          Same rows-not-values dependency.
 *       4. `is_browsing_event()` -> identities.last_browsing_event_ts, read by
 *          stale-high-intent.ts:88-106.
 *     For 2-4 the page_vitals beacon is the replacement row and it carries a
 *     LATER timestamp than any scroll threshold would, so recency improves —
 *     EXCEPT on the pages where that beacon is lost. It is sent on the first
 *     `visibilitychange -> hidden` AND on `pagehide`, one-shot across both, so
 *     the loss window is a browser that fires neither (a crash, an OS kill).
 *     THE CRM-SIDE FOLLOW-UP IS NOT DONE AND IS CROSS-REPO: counts and
 *     distinct-session tiers in (2) and (3) were calibrated against a lane that
 *     included four scroll rows per page view and have not been re-tuned.
 *
 * ORDERING
 * Rendered with `defer` at the end of <body>, in the same position and the
 * same relative order the inline block occupied, so the wrap is installed
 * before DOMContentLoaded and before the customizer's own initialisation —
 * exactly as it was inline. Reads window.__bcCfg / window.__bcAid, both set by
 * the datalayer-init inline core that still runs first in <head>.
 * ===================================================================== */
(function () {
  'use strict';

  var ENDPOINT = 'https://events.beyondcarat.com/collect';
  var SESSION_GAP_MS = 30 * 60 * 1000;   // 30 min idle = new session (matches bc-fingerprint)

  // Payload contract v2 — see CAPTURE_V in assets/bc-tracking-async.js. `event`
  // and `ua` are gone: the door reads `event_name` and the request header.
  var CAPTURE_V = 2;

  // Inner-payload fields copied verbatim from the dataLayer object, per event.
  // An allowlist of NAMES, never a pattern — every entry is a deliberate
  // widening. `field_raw_value` is deliberately absent (a typed email under a
  // non-PII key would persist in clear).
  var SHARED_FIELDS = {
    bc_klaviyo_form_engaged: ['field_name', 'field_type', 'email_current', 'phone_current', 'form_id'],
    // 2026-09-08 — the micro-conversions assets/click-events.js and
    // assets/spin-360.js push. Forwarded to the ledger's funnel lane, which
    // sends them to Meta under these exact names (audience building; owner
    // decision). NO fields cross: contact_form_submit's push carries the typed
    // form values and phone/email clicks carry the address — the name alone is
    // the signal, and the door must never see a raw email under a non-PII key.
    chat_open: [],
    // contact_form_submit: no CLEAR field crosses (still []); the typed email
    // and phone ride ONLY as `email` / `phone`, which the door encrypts at the
    // edge (PII keys, src/worker/pii.ts) — see leadContact() below.
    contact_form_submit: [],
    phone_number_click: [],
    email_click: [],
    affirm_offer_click: [],
    spin_360: []
  };

  // The shared-allowlist events whose browser Meta twin exists in web GTM and
  // must dedup against the server copy by event_id (see the handler below).
  var LEAD_EVENT_IDS = { email_click: 1, affirm_offer_click: 1, contact_form_submit: 1 };

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : null;
  }

  function getOrCreateSessionId() {
    // ONE session rule (round 2). `bc-tracking-async.js` publishes its own
    // `currentSessionId` as `window.__bcSessionId`; it is a `defer` script in
    // <head> and this one is `defer` at the end of <body>, so it has run. The
    // local copy below stays as the fallback for the ways that publish can be
    // missing ENTIRELY: the tracker asset 404s, the CSP refuses it, or it
    // throws before the assignment. NOT because it is "bot-gated" — corrected
    // round 3, 2026-09-06: the file's only bot gate returns from a nested IIFE
    // (bc-tracking-async.js ~:3908, which says so at the call site) and nothing
    // above the publish (~:187) can return at top level. A missing session id
    // must never be an exception, whatever the cause. Reading either one
    // REFRESHES `bc_sess_ts`, which is the same behaviour this copy always had.
    try {
      if (typeof window.__bcSessionId === 'function') return window.__bcSessionId();
    } catch (e) {}
    var KEY = 'bc_sess';
    var TS_KEY = 'bc_sess_ts';
    var now = Date.now();
    try {
      var id = sessionStorage.getItem(KEY);
      var lastTs = parseInt(sessionStorage.getItem(TS_KEY) || '0', 10);
      if (!id || (lastTs && (now - lastTs) > SESSION_GAP_MS)) {
        id = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : (
          'sess-' + now.toString(36) + '-' + Math.random().toString(36).slice(2, 10)
        );
        sessionStorage.setItem(KEY, id);
      }
      sessionStorage.setItem(TS_KEY, String(now));
      return id;
    } catch (e) {
      return 'nosess-' + now.toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }
  }

  // CONSENT (2026-09-29, lane A) — ONE helper, window.bcConsent
  // (snippets/consent-mode-default.liquid). This used to ask
  // userCanBeTracked(), which (a) is Shopify's REGION DEFAULT, not the
  // shopper's answer, and (b) returned `true` whenever the privacy API had not
  // loaded yet — so customizer_view reached the door before the API under an
  // all-denied visitor. Now: an explicit analytics OR marketing "no" stops the
  // relay; unknown is allowed (owner decision); and every send WAITS for the
  // API (bounded, bcConsent WAIT_MS) so the gate is never read before it can
  // answer. The fallback without the helper is fail-closed only on an explicit
  // "no" from the same API.
  function consentApi() {
    var c = window.Shopify && window.Shopify.customerPrivacy;
    return (c && typeof c.currentVisitorConsent === 'function') ? c : null;
  }
  function trackingAllowed() {
    try {
      if (window.bcConsent) return window.bcConsent.captureAllowed();
      var c = consentApi();
      if (c) { var v = c.currentVisitorConsent() || {}; return v.marketing !== 'no' && v.analytics !== 'no'; }
    } catch (e) {}
    return true;
  }
  // The door `consent` stamp — the shape the checkout pixel stamps, so the
  // server's one resolver (bc-tracking src/delivery/consent.ts) reads both.
  function consentStamp() {
    try { if (window.bcConsent) return window.bcConsent.stamp(); } catch (e) {}
    return { state: null };
  }
  function whenConsentSettled(fn) {
    try { if (window.bcConsent) { window.bcConsent.whenSettled(fn); return; } } catch (e) {}
    fn();
  }

  function currentProductHandle() {
    try {
      var m = location.pathname.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  // Same /collect envelope shape as bc-tracking-async.js buildCollectEnvelope
  // (replicated because that builder is private to the async asset's IIFE).
  function buildEnvelope(eventName, payload) {
    var aid = (window.__bcAid) || getCookie('bc_aid') || null;
    var cfg = window.__bcCfg || {};
    // Meta cookies ride in click_ids exactly as bc-tracking-async.js does for
    // page_view/view_item (the server reads fbp/fbc ONLY from click_ids).
    // Without them the funnel lane's CustomizeProduct reaches Meta with no
    // browser identifiers and matches far worse than the sGTM copy did.
    var clickIds = {};
    var fbp = getCookie('_fbp');
    var fbc = getCookie('_fbc');
    if (fbp) clickIds.fbp = fbp;
    if (fbc) clickIds.fbc = fbc;
    return {
      click_ids: clickIds,
      v: 1,
      capture_v: CAPTURE_V,
      source: 'storefront',
      write_key: cfg.writeKey || null,
      event_name: eventName,
      bc_aid: aid,
      bc_aid_candidate: aid ? null : (window.__bcAidCandidate || null),
      consent: consentStamp(),
      url: (window.__bcLandingUrl ? window.__bcLandingUrl() : location.href),
      referrer: document.referrer || '',
      payload: payload
    };
  }

  function sendDoorBeacon(envelope) {
    try {
      var body = JSON.stringify(envelope);
      try {
        if (navigator.sendBeacon) {
          var blob = new Blob([body], { type: 'text/plain' });
          if (navigator.sendBeacon(ENDPOINT, blob)) return;
        }
      } catch (e) { /* fall through to fetch */ }
      fetch(ENDPOINT, {
        method: 'POST',
        body: body,
        headers: { 'Content-Type': 'text/plain' },
        keepalive: true,
        mode: 'cors',
        credentials: 'include'
      }).catch(function () { /* silent — the relay is best-effort */ });
    } catch (e) { /* silent */ }
  }

  // Copy the allowlisted fields for one event. Only the listed keys cross —
  // nothing else on the dataLayer object is forwarded.
  function pickFields(ev, keys) {
    var out = {};
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      out[k] = (ev[k] === undefined || ev[k] === '') ? null : ev[k];
    }
    return out;
  }

  // ---- handler registry ---------------------------------------------------
  // A handler is { match(name) -> bool, build(ev) -> {name, payload, eventId} }.
  // The shared relay owns ONE dataLayer wrap; the customizer asset adds its
  // journey through this registry rather than wrapping push a second time.
  var handlers = [];

  var lastKey = null;
  var lastTs = 0;
  var DEBOUNCE_MS = 300;

  // 300ms identical-consecutive debounce: the same event with the same body
  // inside the window is drag spam, not a distinct journey step. Two DIFFERENT
  // form engagements never collapse.
  function debounced(key) {
    var now = Date.now();
    if (key === lastKey && (now - lastTs) < DEBOUNCE_MS) return true;
    lastKey = key;
    lastTs = now;
    return false;
  }

  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // CONTACT-FORM LEAD CONTACT (2026-10-27). The contact/consultation form push
  // (click-events.js, bc-funnel.js) carries what the shopper typed. Only the
  // email and phone cross, only under the PII key names the door encrypts
  // before anything is stored or queued, and only when plausible. Never
  // `name`, `message` or the x-fb-ud-* copies. Server: the funnel lane hashes
  // them onto the Meta contact_form_submit (same event_id as GTM tag 92) and
  // the identity graph links this browser to them.
  function leadContact(ev) {
    var out = {};
    var em = typeof ev.email === 'string' ? ev.email.trim().toLowerCase() : '';
    if (em && em.length <= 320 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) out.email = em;
    var ph = typeof ev.phone === 'string' ? ev.phone.replace(/[^0-9+]/g, '') : '';
    if (ph.replace(/\D/g, '').length >= 7 && ph.length <= 20) out.phone = ph;
    return out;
  }

  // Held until consent has settled, then gated and stamped with the answer in
  // force AT SEND TIME (a "no" given while the send waited is honoured).
  function send(eventName, payload, eventId) {
    whenConsentSettled(function () {
      if (!trackingAllowed()) return;
      var envelope = buildEnvelope(eventName, payload);
      if (typeof eventId === 'string' && UUID_RE.test(eventId)) envelope.event_id = eventId;
      sendDoorBeacon(envelope);
    });
  }

  function basePayload(eventName) {
    return {
      event_name: eventName,
      bc_aid: (window.__bcAid) || getCookie('bc_aid') || null,
      session_id: getOrCreateSessionId(),
      product_handle: currentProductHandle()
    };
  }

  // The one built-in handler: the shared, every-page allowlist.
  handlers.push({
    match: function (name) {
      return Object.prototype.hasOwnProperty.call(SHARED_FIELDS, name);
    },
    build: function (ev) {
      var name = ev.event;
      var fields = pickFields(ev, SHARED_FIELDS[name]);
      var payload = basePayload(name);
      for (var k in fields) {
        if (Object.prototype.hasOwnProperty.call(fields, k)) payload[k] = fields[k];
      }
      // LEAD EVENTS SHARE THE BROWSER'S event_id (2026-09-29). Web GTM fires the
      // browser Meta copy of these three under the SAME name the funnel lane
      // sends (email_click / affirm_offer_click / contact_form_submit; the Lead
      // Gen ad sets optimise on contact_form_submit) with eventID = the push's
      // uuid. Without that uuid on the door the server copy got a hash-derived
      // id, so Meta could not pair the two and each lead counted twice. The
      // door keys the signal and the Meta event_id on it (src/shared/funnel.ts
      // funnelPlatformEventId).
      if (name === 'contact_form_submit') {
        var contact = leadContact(ev);
        if (contact.email) payload.email = contact.email;
        if (contact.phone) payload.phone = contact.phone;
      }
      var eventId = (LEAD_EVENT_IDS[name] && typeof ev.event_id === 'string' && UUID_RE.test(ev.event_id)) ? ev.event_id : undefined;
      return { name: name, payload: payload, eventId: eventId, key: name + '|' + JSON.stringify(fields) + '|' + (eventId || '') };
    }
  });

  // SIBLING-PRODUCT SOFT NAVIGATION (2026-09-29). A metal or shape switch on
  // the ring customizer swaps to a DIFFERENT product without a document load,
  // and window.bcSoftNav.productView (snippets/gtm-postmessage-bridge.liquid)
  // pushes ONE view_item for it with a fresh uuid event_id. Web GTM fires the
  // browser Meta ViewContent from that push, but a soft navigation never
  // reaches the checkout pixel's door leg, so the ledger had no server twin
  // and Meta ViewContent server coverage fell from ~115% to ~70%. Relay that
  // one push to the door with the SAME event_id (Meta pairs browser + server
  // on it) and the product lines, so the funnel lane's view_item →
  // ViewContent plan sends it with contents ids = variant ids.
  //
  // ONLY pushes stamped bc_soft_nav === 'sibling_product'. A real page load's
  // view_item is bridged from the pixel and carries no bc_soft_nav; the pixel
  // already sends that one to the door itself, so relaying it here would
  // double it. Same-product variant changes (carat, colour, clarity) never
  // call productView, so they never push a view_item at all.
  var SOFT_NAV_ITEM_KEYS = ['item_id', 'item_variant_id', 'item_product_id', 'item_name', 'item_brand',
    'item_category', 'item_variant', 'item_image_url', 'item_url', 'price', 'currency', 'quantity', 'index'];

  function softNavEcommerce(ecom) {
    if (!ecom || typeof ecom !== 'object' || !Array.isArray(ecom.items) || !ecom.items.length) return null;
    var items = [];
    for (var i = 0; i < ecom.items.length && i < 10; i++) {
      var it = ecom.items[i];
      if (!it || typeof it !== 'object') continue;
      var o = {};
      for (var j = 0; j < SOFT_NAV_ITEM_KEYS.length; j++) {
        var k = SOFT_NAV_ITEM_KEYS[j];
        if (it[k] !== undefined && it[k] !== null && it[k] !== '') o[k] = it[k];
      }
      if (o.item_variant_id || o.item_id) items.push(o);
    }
    if (!items.length) return null;
    var out = { items: items };
    if (typeof ecom.currency === 'string') out.currency = ecom.currency;
    if (typeof ecom.value === 'number' && isFinite(ecom.value)) out.value = ecom.value;
    return out;
  }

  handlers.push({
    match: function (name) { return name === 'view_item'; },
    build: function (ev) {
      if (ev.bc_soft_nav !== 'sibling_product') return null;
      if (typeof ev.event_id !== 'string' || !UUID_RE.test(ev.event_id)) return null;
      var ecommerce = softNavEcommerce(ev.ecommerce);
      if (!ecommerce) return null;
      var payload = basePayload('view_item');
      payload.event_id = ev.event_id;
      payload.bc_soft_nav = 'sibling_product';
      payload.page_referrer = typeof ev.page_referrer === 'string' ? ev.page_referrer : null;
      payload.ecommerce = ecommerce;
      return { name: 'view_item', payload: payload, eventId: ev.event_id, key: 'view_item|' + ev.event_id };
    }
  });

  function relay(ev) {
    try {
      // No early consent read here: `send` waits for the API and gates there.
      var name = ev.event;
      for (var i = 0; i < handlers.length; i++) {
        if (!handlers[i].match(name)) continue;
        var built = handlers[i].build(ev);
        if (!built) return;
        if (debounced(built.key)) return;
        send(built.name, built.payload, built.eventId);
        return;
      }
    } catch (e) { /* never break the page */ }
  }

  function isRelayable(ev) {
    if (!ev || typeof ev !== 'object') return false;
    if (typeof ev.event !== 'string') return false;
    for (var i = 0; i < handlers.length; i++) {
      if (handlers[i].match(ev.event)) return true;
    }
    return false;
  }

  // Wrap dataLayer.push. Call the original FIRST (so GTM / the array still get
  // the event untouched), then relay a copy to the door. Wrapping composes
  // safely regardless of GTM load order: whoever wraps last sees the others'
  // push, so the customizer's runtime `window.dataLayer.push(...)` always flows
  // through here. Idempotent — never double-wrap.
  function installWrap() {
    try {
      window.dataLayer = window.dataLayer || [];
      if (window.dataLayer.__bcJourneyWrapped) return;
      var original = window.dataLayer.push;
      window.dataLayer.push = function () {
        var ret = original.apply(this, arguments);
        try {
          for (var i = 0; i < arguments.length; i++) {
            if (isRelayable(arguments[i])) relay(arguments[i]);
          }
        } catch (e) { /* relay must never affect the push result */ }
        return ret;
      };
      window.dataLayer.__bcJourneyWrapped = true;
    } catch (e) { /* silent */ }
  }

  // The public surface the customizer asset (and only it) uses.
  window.BcRelay = {
    on: function (handler) {
      if (handler && typeof handler.match === 'function' && typeof handler.build === 'function') {
        handlers.push(handler);
      }
    },
    sessionId: getOrCreateSessionId,
    productHandle: currentProductHandle,
    basePayload: basePayload,
    allowed: trackingAllowed,
    send: send
  };

  installWrap();
})();
